import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isFundingMethod } from '../discovery/policy.js';
import { withdrawalContextQuery } from '../offramp/context-query.js';
import { bindOnrampCache } from '../operations/cache-lifecycle.js';
import { useAccountAvailableMethods } from './available-methods-react.js';
import { accountFundingMethods } from './method-limits.js';
import { readPaymentMethodAllowances } from './payment-method-allowances.js';
import { accountTransactionBounds } from './transaction-bounds.js';
export function useAccountPaymentMethods(options) {
  const $ = _c(76);
  const available = useAccountAvailableMethods(options);
  const cache = useQueryClient();
  const enabled = options.enabled && options.masterEnabled;
  let t0;
  if ($[0] !== options.connection) {
    t0 = accountFundingMethods(options.connection).flatMap(_temp);
    $[0] = options.connection;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const methods = t0;
  const connection = options.connection;
  const t1 = connection.verification?.customerId;
  const t2 = connection.quoteContext?.context.country;
  const t3 = connection.quoteContext?.context.subdivision;
  const t4 = connection.quoteContext?.context.destination;
  const t5 = connection.quoteContext?.context.asset;
  let t6;
  if ($[2] !== connection.quoteContext?.savedCards) {
    t6 = connection.quoteContext?.savedCards?.filter(_temp2).map(_temp3);
    $[2] = connection.quoteContext?.savedCards;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== connection.guest || $[5] !== methods || $[6] !== options.client.cacheKey || $[7] !== options.currency || $[8] !== options.decimals || $[9] !== options.sessionProofId || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = ["onramp", options.client.cacheKey, "payment-method-allowances", options.sessionProofId, options.currency, options.decimals, methods, t1, connection.guest, t2, t3, t4, t5, t6];
    $[4] = connection.guest;
    $[5] = methods;
    $[6] = options.client.cacheKey;
    $[7] = options.currency;
    $[8] = options.decimals;
    $[9] = options.sessionProofId;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== methods || $[18] !== options) {
    t8 = t9 => {
      const {
        signal
      } = t9;
      return readPaymentMethodAllowances({
        ...options,
        methods
      }, signal);
    };
    $[17] = methods;
    $[18] = options;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = enabled && available.direction === "onramp" && methods.length > 0;
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      persist: false
    };
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9,
      staleTime: 60000,
      retry: false,
      refetchOnWindowFocus: false,
      meta: t10
    };
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const allowances = useQuery(t11);
  let t12;
  if ($[25] !== options.client.cacheKey || $[26] !== options.currency) {
    t12 = ["onramp", options.client.cacheKey, "transaction-bounds", options.currency];
    $[25] = options.client.cacheKey;
    $[26] = options.currency;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== options.client || $[29] !== options.currency) {
    t13 = t14 => {
      const {
        signal: signal_0
      } = t14;
      return options.client.getTransactionBounds(options.currency, signal_0);
    };
    $[28] = options.client;
    $[29] = options.currency;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = enabled && available.direction === "onramp";
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      persist: false
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t12 || $[33] !== t13 || $[34] !== t14) {
    t16 = {
      queryKey: t12,
      queryFn: t13,
      enabled: t14,
      staleTime: 60000,
      retry: false,
      refetchOnWindowFocus: false,
      meta: t15
    };
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const bounds = useQuery(t16);
  let t17;
  if ($[36] !== options.client || $[37] !== options.currency || $[38] !== options.surface) {
    t17 = withdrawalContextQuery(options.client, options.currency, options.surface);
    $[36] = options.client;
    $[37] = options.currency;
    $[38] = options.surface;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = enabled && available.direction === "offramp";
  let t19;
  if ($[40] !== t17 || $[41] !== t18) {
    t19 = {
      ...t17,
      enabled: t18,
      retry: false,
      refetchOnWindowFocus: false
    };
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const withdrawal = useQuery(t19);
  let t20;
  let t21;
  if ($[43] !== cache || $[44] !== options.client) {
    t20 = () => {
      bindOnrampCache(options.client, cache);
    };
    t21 = [options.client, cache];
    $[43] = cache;
    $[44] = options.client;
    $[45] = t20;
    $[46] = t21;
  } else {
    t20 = $[45];
    t21 = $[46];
  }
  useEffect(t20, t21);
  const failed = allowances.isError || bounds.isError;
  let t22;
  let t23;
  if ($[47] !== failed || $[48] !== options.client) {
    t22 = () => {
      if (failed) {
        options.client.observeFunnel?.({
          stage: "profile",
          outcome: "error",
          errorCode: "onramp_refresh_failed"
        });
      }
    };
    t23 = [options.client, failed];
    $[47] = failed;
    $[48] = options.client;
    $[49] = t22;
    $[50] = t23;
  } else {
    t22 = $[49];
    t23 = $[50];
  }
  useEffect(t22, t23);
  const t24 = available.refreshing || (available.direction === "onramp" ? allowances.isFetching || bounds.isFetching : withdrawal.isFetching);
  let t25;
  if ($[51] !== allowances || $[52] !== available || $[53] !== bounds || $[54] !== enabled || $[55] !== methods || $[56] !== options.currency || $[57] !== options.fx || $[58] !== t24 || $[59] !== withdrawal.data) {
    let t26;
    if ($[61] !== allowances.isPending || $[62] !== available.direction || $[63] !== enabled || $[64] !== methods) {
      t26 = enabled && available.direction === "onramp" && allowances.isPending ? methods : [];
      $[61] = allowances.isPending;
      $[62] = available.direction;
      $[63] = enabled;
      $[64] = methods;
      $[65] = t26;
    } else {
      t26 = $[65];
    }
    let t27;
    if ($[66] !== available.direction || $[67] !== bounds.data || $[68] !== options.currency || $[69] !== options.fx || $[70] !== withdrawal.data) {
      t27 = accountTransactionBounds({
        direction: available.direction,
        buy: bounds.data,
        sell: withdrawal.data,
        currency: options.currency,
        fx: options.fx
      });
      $[66] = available.direction;
      $[67] = bounds.data;
      $[68] = options.currency;
      $[69] = options.fx;
      $[70] = withdrawal.data;
      $[71] = t27;
    } else {
      t27 = $[71];
    }
    let t28;
    if ($[72] !== available.direction || $[73] !== available.methods || $[74] !== withdrawal.data?.methods) {
      t28 = available.direction === "onramp" ? available.methods.flatMap(_temp4) : withdrawal.data?.methods.map(_temp5) ?? [];
      $[72] = available.direction;
      $[73] = available.methods;
      $[74] = withdrawal.data?.methods;
      $[75] = t28;
    } else {
      t28 = $[75];
    }
    t25 = {
      ...available,
      refreshing: t24,
      async refresh() {
        if (!enabled) {
          return;
        }
        if (available.direction === "offramp") {
          await available.refresh();
        } else {
          await Promise.all([available.refresh(), bounds.refetch({
            cancelRefetch: false
          }), ...(methods.length > 0 ? [allowances.refetch({
            cancelRefetch: false
          })] : [])]);
        }
      },
      allowances: available.direction === "onramp" ? allowances.data : undefined,
      limitsLoading: enabled && available.direction === "onramp" && methods.length > 0 && allowances.isPending,
      limitsError: available.direction === "onramp" && allowances.isError,
      loadingMethodIds: t26,
      transactionBounds: t27,
      boundedMethodIds: t28
    };
    $[51] = allowances;
    $[52] = available;
    $[53] = bounds;
    $[54] = enabled;
    $[55] = methods;
    $[56] = options.currency;
    $[57] = options.fx;
    $[58] = t24;
    $[59] = withdrawal.data;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  return t25;
}
function _temp5(method_1) {
  return method_1.method;
}
function _temp4(method_0) {
  return isFundingMethod(method_0.id) ? [method_0.id] : [];
}
function _temp3(card_0) {
  return card_0.id;
}
function _temp2(card) {
  return card.availability.active;
}
function _temp(method) {
  return method.available && isFundingMethod(method.id) ? [method.id] : [];
}